import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  FileText, Upload, CheckCircle2, X,
  Loader2, TrendingUp, Euro, Building,
} from 'lucide-react';
import SuperadminCockpit from '../components/SuperadminCockpit';


function KpiCard({ icon: Icon, label, value, color = '#D44E20' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
    </div>
  );
}


function UploadInvoiceModal({ request, onClose, onDone }) {
  const [pdf, setPdf] = useState(null);
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!pdf || !number) {
      toast.error('Carica un PDF e inserisci il numero fattura');
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append('pdf', pdf);
    fd.append('fiscal_invoice_number', number);
    fd.append('fiscal_invoice_date', date);
    fd.append('notes', notes);
    try {
      await adminApi.post(`/superadmin/invoice-requests/${request.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fattura caricata');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upload-invoice-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1A1A1A]">Carica fattura fiscale</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-[#1A1A1A]"><X size={18} /></button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs">
          <p><b>Brand:</b> {request.brand_name}</p>
          <p><b>Ricevuta:</b> {request.receipt_number}</p>
          <p><b>Importo:</b> {Number(request.amount_total || 0).toFixed(2)} €</p>
          <p><b>P.IVA:</b> {request.buyer_snapshot?.vat_number || '—'}</p>
          {request.buyer_snapshot?.sdi_code && <p><b>SDI:</b> {request.buyer_snapshot.sdi_code}</p>}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Numero fattura (reale)*</label>
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Es. 2026/00123"
              data-testid="fiscal-number-input"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Data fattura*</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">PDF fattura*</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdf(e.target.files?.[0] || null)}
              data-testid="fiscal-pdf-input"
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-[#1A1A1A] file:font-semibold hover:file:bg-slate-200"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Note (opzionali)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-50">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="confirm-upload-invoice"
            className="flex-1 px-4 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} /> Carica e invia</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function SettingsPanel() {
  const [form, setForm] = useState({
    vat_rate: 22,
    notify_email: '',
    seller: {
      company_name: '', vat_number: '', tax_code: '',
      address: '', city: '', zip_code: '', country: 'Italia',
      email: '', phone: '',
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/superadmin/billing/settings').then(r => {
      setForm({
        vat_rate: r.data.vat_rate ?? 22,
        notify_email: r.data.notify_email || '',
        seller: { ...form.seller, ...(r.data.seller || {}) },
      });
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put('/superadmin/billing/settings', form);
      toast.success('Impostazioni salvate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" size={24} /></div>;

  const sellerField = (key, label, placeholder = '') => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input value={form.seller[key] || ''} onChange={e => setForm({ ...form, seller: { ...form.seller, [key]: e.target.value } })}
        placeholder={placeholder} data-testid={`seller-${key}`}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="billing-settings-panel">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-[#1A1A1A]">Impostazioni fatturazione</h3>
        <button onClick={save} disabled={saving} data-testid="save-billing-settings"
          className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:shadow-md disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salva'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Aliquota IVA % (per ricevute)</label>
          <input type="number" step="0.01" value={form.vat_rate} onChange={e => setForm({ ...form, vat_rate: parseFloat(e.target.value) || 0 })}
            data-testid="vat-rate-input"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Email notifiche richieste fattura</label>
          <input type="email" value={form.notify_email} onChange={e => setForm({ ...form, notify_email: e.target.value })}
            placeholder="billing@lazo.app" data-testid="notify-email-input"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
        </div>
      </div>

      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Dati venditore (Lazo)</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sellerField('company_name', 'Ragione sociale', 'Lazo Srl')}
        {sellerField('vat_number', 'P.IVA', 'IT12345678901')}
        {sellerField('tax_code', 'Codice fiscale')}
        {sellerField('email', 'Email')}
        {sellerField('phone', 'Telefono')}
        <div className="md:col-span-2">{sellerField('address', 'Indirizzo')}</div>
        {sellerField('zip_code', 'CAP')}
        {sellerField('city', 'Città')}
        {sellerField('country', 'Paese')}
      </div>
    </div>
  );
}


export default function SuperadminBilling() {
  const [tab, setTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    const [r, o] = await Promise.all([
      adminApi.get(`/superadmin/invoice-requests?status=${tab}`),
      adminApi.get('/superadmin/billing/overview'),
    ]);
    setRequests(r.data.requests || []);
    setOverview(o.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const cancelRequest = async (r) => {
    const reason = window.prompt('Motivo annullamento?', '');
    if (reason === null) return;
    try {
      await adminApi.post(`/superadmin/invoice-requests/${r.id}/cancel`, { reason });
      toast.success('Richiesta annullata');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="superadmin-billing-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Fatturazione Lazo</h1>
        <p className="text-sm text-slate-500 mt-1">Gestisci richieste di fattura, monitora MRR e impostazioni.</p>
      </div>

      {/* KPI */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={Euro} label="MRR" value={`${overview.mrr.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`} color="#D44E20" />
          <KpiCard icon={TrendingUp} label="ARR" value={`${overview.arr.toLocaleString('it-IT', { maximumFractionDigits: 0 })} €`} color="#8B5CF6" />
          <KpiCard icon={Building} label="Abbonamenti attivi" value={overview.active_subscriptions} color="#10B981" />
          <KpiCard icon={FileText} label="Fatture in attesa" value={overview.pending_invoice_requests} color="#F59E0B" />
        </div>
      )}

      <SuperadminCockpit />

      <SettingsPanel />

      <div className="flex items-center gap-1 border-b border-slate-200 mb-4">
        {[
          { key: 'pending', label: 'In attesa', icon: FileText },
          { key: 'issued', label: 'Emesse', icon: CheckCircle2 },
          { key: 'cancelled', label: 'Annullate', icon: X },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`superadmin-invoices-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all ${
              tab === key ? 'border-[#D44E20] text-[#D44E20]' : 'border-transparent text-slate-500 hover:text-[#1A1A1A]'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" size={24} /></div>
        ) : requests.length === 0 ? (
          <p className="text-center py-10 text-slate-500 text-sm">Nessuna richiesta in questo stato.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="superadmin-requests-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Brand</th>
                  <th className="text-left px-4 py-3">Ricevuta</th>
                  <th className="text-left px-4 py-3">P.IVA</th>
                  <th className="text-left px-4 py-3">Richiesta</th>
                  <th className="text-left px-4 py-3">Nr fiscale</th>
                  <th className="text-right px-4 py-3">Importo</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`request-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">{r.brand_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.receipt_number}</td>
                    <td className="px-4 py-3 text-slate-600">{r.buyer_snapshot?.vat_number || '—'}</td>
                    <td className="px-4 py-3">{new Date(r.requested_at).toLocaleDateString('it-IT')}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.fiscal_invoice_number || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{Number(r.amount_total || 0).toFixed(2)} €</td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setUploadTarget(r)} data-testid={`upload-invoice-${r.id}`}
                            className="text-xs px-3 py-1.5 rounded-lg bg-[#D44E20] text-white hover:shadow-md font-semibold flex items-center gap-1.5">
                            <Upload size={12} /> Carica fattura
                          </button>
                          <button onClick={() => cancelRequest(r)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold">
                            Annulla
                          </button>
                        </div>
                      )}
                      {r.status === 'issued' && (
                        <span className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 font-semibold flex items-center justify-end gap-1 w-fit ml-auto">
                          <CheckCircle2 size={12} /> Emessa il {r.issued_at ? new Date(r.issued_at).toLocaleDateString('it-IT') : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {uploadTarget && (
        <UploadInvoiceModal request={uploadTarget} onClose={() => setUploadTarget(null)} onDone={load} />
      )}
    </div>
  );
}
